import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Modal, Select, Button, Input, notification, Checkbox, Slider, Badge } from "antd";
import _ from "lodash";
import { useSelector, useDispatch } from "react-redux";
import { createMarkerExport, requestContinuousObservationsReport } from "../redux/actions/index";
import { combineContinuousObservations, videoTimestampExtractor } from "./util/PlaylistUtils";
import { getRoutePosAtTimestamp } from "./util/GeometryUtilsV2";
import { routeGeomSelector } from "redux/selectors";

const superAdminSelector = (state) => state.userDetails.userConfig.super_admin;
const sessionIDSelector = (state) => _.get(state, ["playlist", "data", "routeID"]);
const userEmailSelector = (state) => state.userDetails.email;
const markerSelector = (state) => {
    const sessionID = _.get(state, ["playlist", "data", "routeID"]);
    return _.get(state.markers.perSession, [sessionID], []);
};
const thresholdFilterSelector = (state) => state.markerThresholdFilters;
const sessionObservationsSelector = (state) => state.sessionObservations;
const detectionTypesSelector = (state) => state.detectionTypes;

const emptyObject = {};

const playlistSelector = (state) => state.playlist.data.video;
const routeSystemIDSelector = (state) => state.playlist.data.system_id;

const DEFAULT_CONTINUOUS_REPORT_FORM = {
    end_ts: null,
    filter_options: null,
    review_status: null,
    score: null,
    sessionID: null,
    start_ts: null,
    types: [],
    name: null,
};

const CONTINUOUS_OBSERVATION_REPORT_TYPES = {
    "Scrap Rail": "enable_cont_obser_report_scrap_rail",
    low_ballast: "enable_cont_obser_report_low_ballast",
    Troughing: "enable_cont_obser_report_troughing",
};

export const OBSERVATION_EXPORT_BLACKLIST = ["vegetation_encroachment", "low_ballast"];

const MarkerExportModal = ({ closeModal, markerType }) => {
    const dispatch = useDispatch();

    const userIsSuperAdmin = useSelector(superAdminSelector);
    const sessionID = useSelector(sessionIDSelector);
    const userEmail = useSelector(userEmailSelector);
    const markers = useSelector(markerSelector);
    const detectionTypes = useSelector(detectionTypesSelector);
    const thresholdFilters = useSelector(thresholdFilterSelector);
    const routeGeometry = useSelector(routeGeomSelector);
    const sessionObservations = useSelector(sessionObservationsSelector);
    const sessionPlaylist = useSelector(playlistSelector);
    const routeSystemID = useSelector(routeSystemIDSelector);

    const [exportMarkerType, setExportMarkupType] = useState(markerType);
    const [reviewStatus, setReviewStatus] = useState(["all"]);
    const [timeSelection, setTimeSelection] = useState([]);

    const [recipient, setRecipient] = useState(userEmail);
    const [name, setName] = useState("");
    const [exportLoading, setExportLoading] = useState(false);
    const [allSessionsToggle, setAllSessionsToggle] = useState(false);
    const [allSessionsELR, setAllSessionsELR] = useState("");
    const [continuousObservationReport, setContinuousObservationReport] = useState(false);
    const [continuousReportForm, setContinuousReportForm] = useState(DEFAULT_CONTINUOUS_REPORT_FORM);

    const currentRouteID = useSelector((state) => state.playlist.data.routeID);
    const defaultMarkers = useSelector((state) => state.defaultMarkersThresholds[currentRouteID] || emptyObject);

    useEffect(() => {
        if (sessionID) {
            setContinuousReportForm((curr) => ({ ...curr, sessionID }));
        }
    }, [sessionID]);

    useEffect(() => {
        if (exportMarkerType) {
            const [markerType, isObservation, isContinuous] = exportMarkerType.split("_");
            setContinuousObservationReport(isContinuous === "continuous");

            if (isObservation) {
                setAllSessionsToggle(false);
            }

            const types = [];
            if (isContinuous) {
                if (_.has(CONTINUOUS_OBSERVATION_REPORT_TYPES, markerType)) {
                    types.push(CONTINUOUS_OBSERVATION_REPORT_TYPES[markerType]);
                }
            }
            setContinuousReportForm((curr) => ({ ...curr, types }));
        }
    }, [exportMarkerType, sessionID]);

    const onClick = () => {
        let filters = {
            types: exportMarkerType,
        };

        if (reviewStatus && reviewStatus[0] !== "all") {
            filters.review_status_filters = reviewStatus;
        }

        filters.score_filters = {};
        detectionTypes
            .filter((type) => {
                return type.min_score || type.max_score;
            })
            .forEach((type) => {
                const defValue = _.get(defaultMarkers, [type.type], type.default_score);
                const filterValue = _.get(thresholdFilters, [type.type], defValue);
                filters.score_filters[type.type] = filterValue;
            });

        if (allSessionsToggle) {
            filters.exportELR = allSessionsELR;
        }

        setExportLoading(true);
        dispatch(createMarkerExport(sessionID, name, recipient, filters)).then((success) => {
            setExportLoading(false);
            if (success) {
                notification.success({
                    message: "Successfully triggered export",
                });
                closeModal();
            } else {
                notification.error({
                    message: "Error creating export",
                });
            }
        });
    };

    const requestContinuousObservationReport = () => {
        const form = {
            ...continuousReportForm,
            name: name.trim(),
            email: recipient.trim(),
            start_ts: continuousReportForm.start_ts ? Math.round(continuousReportForm.start_ts / 1000) : null,
            end_ts: continuousReportForm.end_ts ? Math.round(continuousReportForm.end_ts / 1000) : null,
        };

        if (reviewStatus && reviewStatus.length && reviewStatus[0] !== "all") {
            form.review_status = reviewStatus.join(", ");
        }

        dispatch(
            requestContinuousObservationsReport(form, (response) => {
                if (response.success) {
                    notification.success({
                        message: response.message,
                    });
                    closeModal();
                } else {
                    notification.error({
                        message: "Error creating export",
                    });
                }
            }),
        );
    };

    const combinedObservations = useMemo(() => {
        return combineContinuousObservations(sessionObservations);
    }, [sessionObservations]);

    const onReviewStatusChange = useCallback(
        (options) => {
            if ((options.includes("all") && !reviewStatus.includes("all")) || options.length === 0) {
                options = ["all"];
            } else if (reviewStatus.includes("all") && options.length) {
                const index = options.indexOf("all");
                if (index > -1) {
                    options.splice(index, 1);
                }
            }
            setReviewStatus(options);
        },
        [reviewStatus],
    );

    const exportCount = useMemo(() => {
        if (exportMarkerType) {
            if (!exportMarkerType.includes("_observations")) {
                const filteredMarkers = markers.filter((marker) => {
                    if (marker.name !== exportMarkerType) {
                        return false;
                    }

                    if (reviewStatus && reviewStatus[0] !== "all") {
                        if (!reviewStatus.includes(marker.review_status)) {
                            return false;
                        }
                    }

                    let detectionType = _.find(detectionTypes, function (ann) {
                        return ann.type === marker.name;
                    });

                    if (!detectionType || detectionType.display_type === "never") {
                        return false;
                    }

                    const defValue = _.get(defaultMarkers, [detectionType.type], detectionType.default_score);

                    if (!_.isNil(thresholdFilters[detectionType.type])) {
                        if (marker.score && marker.score < thresholdFilters[detectionType.type]) {
                            return false;
                        }
                    } else if (defValue) {
                        if (marker.score && marker.score < defValue) {
                            return false;
                        }
                    }
                    return true;
                });

                return filteredMarkers.length;
            } else {
                const [selected_start_ts, selected_end_ts] = timeSelection;

                const filteredObservations = combinedObservations.filter((observation) => {
                    if (exportMarkerType && !exportMarkerType.includes(`${observation.class}_observations`)) {
                        return false;
                    }

                    if (reviewStatus && reviewStatus[0] !== "all") {
                        if (!reviewStatus.includes(observation.review_status)) {
                            return false;
                        }
                    }

                    if ((selected_start_ts && observation.end_ts < selected_start_ts) || (selected_end_ts && observation.start_ts > selected_end_ts)) {
                        return false;
                    }

                    return true;
                });

                return filteredObservations.length;
            }
        }
    }, [markers, timeSelection, combinedObservations, exportMarkerType, reviewStatus, detectionTypes, defaultMarkers, thresholdFilters]);

    const markerTypes = useMemo(() => {
        const observationTypes = _.uniqBy(sessionObservations, "class");
        const markerTypes = _.map(_.uniqBy(markers, "name"), "name").filter((type) => _.findIndex(observationTypes, { class: type }));

        const markerOptions = markerTypes.map((type) => (
            <Select.Option
                label={type}
                value={type}
                key={type}>
                {type}
            </Select.Option>
        ));

        const observationOptions = observationTypes
            .filter((type) => !OBSERVATION_EXPORT_BLACKLIST.includes(type.class))
            .map((type) => (
                <Select.Option
                    label={`${type.display_name || type.class}`}
                    value={`${type.class}_observations${type.continuous ? "_continuous" : ""}`}
                    key={type.class}>
                    {type.display_name || type.class}
                </Select.Option>
            ));

        return [...markerOptions, ...observationOptions];
    }, [markers, sessionObservations]);

    const submitDisabled = useMemo(() => {
        return name === "" || recipient === "" || exportMarkerType === undefined || (allSessionsToggle && !allSessionsELR);
    }, [allSessionsELR, allSessionsToggle, exportMarkerType, name, recipient]);

    const elrOptions = useMemo(() => {
        const routePositionData = _.get(routeGeometry, ["distance_to_route_position"], []);

        return _.uniq(routePositionData.map((pos) => pos[1])).map((elr) => {
            return (
                <Select.Option
                    value={elr}
                    key={elr}>
                    {elr}
                </Select.Option>
            );
        });
    }, [routeGeometry]);

    const routeStartAndEndTimestamps = useMemo(() => {
        const sourceVideo = _.get(sessionPlaylist, [0], []);
        if (sourceVideo && sourceVideo.length) {
            const startTS = parseInt(videoTimestampExtractor(sourceVideo[0]) * 1000);
            const endTS = parseInt(videoTimestampExtractor(sourceVideo[sourceVideo.length - 1]) * 1000);

            setTimeSelection([startTS, endTS]);
            return {
                startTS,
                endTS,
            };
        }
        return {};
    }, [sessionPlaylist]);

    const sliderRange = useMemo(() => {
        const { startTS, endTS } = routeStartAndEndTimestamps;

        if (timeSelection && timeSelection.length) {
            const formatTooltip = (ts) => {
                const dateObject = new Date(ts);
                const hours = ("0" + dateObject.getHours()).slice(-2);
                const minutes = ("0" + dateObject.getMinutes()).slice(-2);
                const seconds = ("0" + dateObject.getSeconds()).slice(-2);

                const position = getRoutePosAtTimestamp(ts, routeGeometry, routeSystemID);
                console.log("debug position", { ts, routeGeometry, routeSystemID, position });
                if (!position) {
                    return null;
                }
                // const position = _.minBy(routeLocationData, (location) => Math.abs(location.timestamp - ts));
                // const elrMileAndChain = ELRMileAndChain.from_fields(
                //     "ELR Mile & Chain",
                //     "elr_meterage",
                //     {
                //         ELR: position.route,
                //         METERAGE: position.location[1],
                //         TRACK: position.location[2],
                //     },
                //     [],
                //     false,
                // );

                return (
                    <div>
                        <p style={{ margin: "1px", textAlign: "center", fontSize: "12px" }}>
                            {hours}:{minutes}:{seconds}
                        </p>
                        <p style={{ margin: "0", textAlign: "center", fontSize: "12px" }}>
                            {position.route} - {position.to_string()}
                        </p>
                    </div>
                );
            };

            return (
                <Slider
                    style={{ marginTop: "2px" }}
                    tooltipPlacement="bottom"
                    range
                    min={startTS}
                    max={endTS}
                    tipFormatter={(e) => formatTooltip(e)}
                    onChange={(e) => setTimeSelection(e)}
                    value={timeSelection}
                />
            );
        }

        return null;
    }, [routeStartAndEndTimestamps, timeSelection, routeGeometry, routeSystemID]);

    useEffect(() => {
        if (timeSelection && timeSelection.length) {
            setContinuousReportForm((curr) => ({ ...curr, start_ts: timeSelection[0], end_ts: timeSelection[1] }));
        }
    }, [timeSelection]);

    return (
        <Modal
            title="Export Detections"
            visible={true}
            dropdownMatchSelectWidth={false}
            onOk={closeModal}
            onCancel={closeModal}
            footer={[
                <Button
                    key="cancel"
                    onClick={closeModal}>
                    Cancel
                </Button>,
                <Button
                    key="primary"
                    type="primary"
                    loading={exportLoading}
                    onClick={continuousObservationReport ? requestContinuousObservationReport : onClick}
                    disabled={submitDisabled}>
                    Create Export
                </Button>,
            ]}>
            <div className="MarkerExport">
                <div className="FormItem">
                    <label>Export Name</label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="ExportInput"
                    />
                </div>
                <div className="FormItem">
                    <label>Email Recipient</label>
                    <Input
                        value={recipient}
                        onChange={(e) => setRecipient(e.target.value)}
                        className="ExportInput"
                    />
                </div>
                <div className="FormItem">
                    <label>Detection Type</label>
                    <Select
                        placeholder="Select a detection type"
                        value={exportMarkerType}
                        onChange={setExportMarkupType}
                        className="ExportInput">
                        {markerTypes}
                    </Select>
                </div>
                {continuousObservationReport && (
                    <div className="FormItem">
                        <label>
                            Filter Options <span style={{ fontSize: "11px", fontStyle: "italic" }}>(You can select multiple)</span>
                        </label>
                        <Select
                            mode="multiple"
                            className="ExportInput"
                            placeholder="e.g. scrap, future_works, strategic..."
                            disabled={exportMarkerType && exportMarkerType !== "Scrap Rail_observations_continuous"}
                            onChange={(e) => {
                                if (e.length) {
                                    setContinuousReportForm({ ...continuousReportForm, filter_options: e.join(", ") });
                                } else {
                                    setContinuousReportForm({ ...continuousReportForm, filter_options: null });
                                }
                            }}
                            value={continuousReportForm.filter_options ? continuousReportForm.filter_options.split(", ") : []}>
                            {[
                                { label: "Future works", value: "future_works" },
                                { label: "Scrap", value: "scrap" },
                                { label: "Strategic", value: "strategic" },
                            ].map((option) => {
                                return (
                                    <Select.Option
                                        key={option.value}
                                        value={option.value}>
                                        {option.label}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>
                )}
                <div className="FormItem">
                    <label>Review status</label>
                    <Select
                        value={reviewStatus}
                        onChange={onReviewStatusChange}
                        className="ExportInput"
                        mode="multiple">
                        <Select.Option
                            value={"all"}
                            key="all">
                            All
                        </Select.Option>
                        <Select.Option
                            value={1}
                            key="verified">
                            Verified
                        </Select.Option>
                        <Select.Option
                            value={2}
                            key="verifiedandhidden">
                            Verified & Hidden
                        </Select.Option>
                        <Select.Option
                            value={0}
                            key="needs_review">
                            Needs Review
                        </Select.Option>
                        <Select.Option
                            value={3}
                            key="rejected">
                            Rejected
                        </Select.Option>
                    </Select>
                </div>
                {continuousObservationReport && (
                    <div className="FormItem">
                        <label>Start / End Time</label>
                        {sliderRange}
                    </div>
                )}
                {userIsSuperAdmin && exportMarkerType && !exportMarkerType.includes("_observations") && (
                    <>
                        <label className="ExportCheckboxLabel">
                            <Checkbox
                                checked={allSessionsToggle}
                                onChange={(e) => setAllSessionsToggle(e.target.checked)}
                            />
                            Include Detections from other sessions
                            <Badge
                                count={"ALPHA"}
                                style={{ color: "#8f6bac", backgroundColor: "#d7cbfa", fontSize: 10, fontWeight: 600 }}
                            />
                        </label>

                        {allSessionsToggle && (
                            <div className="FormItem">
                                <label>ELR To Export</label>
                                <Select
                                    style={{ width: 160 }}
                                    disabled={!allSessionsToggle}
                                    value={allSessionsELR}
                                    onChange={setAllSessionsELR}>
                                    {elrOptions}
                                </Select>
                            </div>
                        )}
                    </>
                )}
                {!allSessionsToggle && exportMarkerType && (
                    <span style={{ marginTop: 6, textAlign: "right" }}>
                        <strong>{exportCount}</strong> markers expected
                    </span>
                )}
            </div>
        </Modal>
    );
};

export default MarkerExportModal;
